import { useEffect, useMemo, useState } from 'react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { sendEmailTemplateTestMail } from '../services/emailTemplatesService'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'

import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'

type Props = {
  isOpen: boolean
  onClose: () => void
  testType: 'Email' | 'SMS'
  workflowType: string
  availableVariables?: Record<string, string> | null
  /** Current template body (HTML) — same source as Angular `templateDetail.email_body` for preview. */
  emailBody: string
  /** Current SMS body — same as Angular `templateDetail.sms_body` for preview. */
  smsBody: string
}

function validateSmsPhone(raw: string): string {
  const cleaned = raw.replace(/\D/g, '')
  const match = cleaned.match(/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/)
  if (!match) {
    return 'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX]'
  }
  return ''
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Mirrors Angular `email-send.component.ts` `getFrameContent()`: strip `$`, `\n` → `<br>`, then substitute param keys.
 */
function getFrameContent(
  templateBody: string,
  bodyParams: Record<string, string>,
  variableKeys: string[],
): string {
  let bindContent = templateBody
  if (!bindContent) {
    return '<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body></body></html>'
  }
  bindContent = bindContent.replace(/\$/g, '')
  bindContent = bindContent.replace(/\n/g, '<br>')
  for (const key of variableKeys) {
    const v = (bodyParams[key] ?? '').trim()
    if (v) {
      try {
        bindContent = bindContent.replace(new RegExp(escapeRegExp(key), 'g'), v)
      } catch {
        // ignore
      }
    }
  }
  if (bindContent.trim().toLowerCase().includes('<!doctype') || /<html[\s>]/i.test(bindContent)) {
    return bindContent
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body>${bindContent}</body></html>`
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i

export function SendTestMailModal({
  isOpen,
  onClose,
  testType,
  workflowType,
  availableVariables,
  emailBody,
  smsBody,
}: Props) {
  const [toAddress, setToAddress] = useState('')
  const [bodyParams, setBodyParams] = useState<Record<string, string>>({})
  const [localError, setLocalError] = useState('')
  const [sending, setSending] = useState(false)
  const [isPreview, setIsPreview] = useState(false)

  const variableKeys = useMemo(() => {
    if (availableVariables && typeof availableVariables === 'object') {
      return Object.keys(availableVariables)
    }
    return [] as string[]
  }, [availableVariables])

  useEffect(() => {
    if (!isOpen) return
    setToAddress('')
    setLocalError('')
    setSending(false)
    setIsPreview(false)
    setBodyParams(Object.fromEntries(variableKeys.map((k) => [k, ''])))
  }, [isOpen, variableKeys])

  const templateBodyForPreview = testType === 'SMS' ? smsBody : emailBody
  const previewSrcDoc = useMemo(
    () => getFrameContent(templateBodyForPreview || '', bodyParams, variableKeys),
    [templateBodyForPreview, bodyParams, variableKeys],
  )

  const submitDisabled =
    sending ||
    !toAddress.trim() ||
    (testType === 'Email' && !EMAIL_RE.test(toAddress.trim())) ||
    (testType === 'SMS' && validateSmsPhone(toAddress) !== '')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError('')
    if (!toAddress.trim()) {
      setLocalError(testType === 'SMS' ? 'Phone is required.' : 'Email is required.')
      return
    }
    if (testType === 'SMS') {
      const emsg = validateSmsPhone(toAddress)
      if (emsg) {
        setLocalError(emsg)
        return
      }
    } else if (!EMAIL_RE.test(toAddress.trim())) {
      setLocalError('Enter a valid email address.')
      return
    }
    setSending(true)
    try {
      const cleaned: Record<string, string> = {}
      for (const [k, v] of Object.entries(bodyParams)) {
        if (v && String(v).trim()) cleaned[k] = String(v).trim()
      }
      const payload = {
        workflow: workflowType,
        toAddress: testType === 'SMS' ? toAddress.replace(/\D/g, '') : toAddress.trim(),
        bodyParams: cleaned,
        ...(testType === 'SMS' ? { testsms: '1' as const } : {}),
      }
      await sendEmailTemplateTestMail(payload)
      showSuccess('Sent Successfully!')
      onClose()
    } catch (err) {
      showApiError(err)
    } finally {
      setSending(false)
    }
  }

  const testTypeLabel = testType
  return (
    <OnScreenModal
      isOpen={isOpen}
      title={testType === 'SMS' ? 'SMS Send' : 'Email Send'}
      onClose={onClose}
      maxWidth="max-w-lg"
      bodyScrollOnly
      titleDataTestId="send-test-mail-title"
      overlayDataTestId="send-test-mail-overlay"
      panelDataTestId="send-test-mail-panel"
      footer={
        <div
          className="flex flex-wrap items-center justify-center gap-3"
          data-testid="send-test-mail-footer"
        >
          <button
            type="submit"
            form="send-test-mail-form"
            disabled={submitDisabled || isPreview}
            className={cn(primaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="send-test-submit"
          >
            {sending ? 'Sending…' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="send-test-cancel"
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="w-full max-w-[500px]">
        {localError && (
          <p className="mb-3 text-sm text-red-600" role="alert">
            {localError}
          </p>
        )}

        <div className="mb-4 flex flex-row items-baseline gap-2">
          <button
            type="button"
            onClick={() => setIsPreview(false)}
            className={cn(
              'cursor-pointer border-0 bg-transparent p-0 text-left text-sm',
              !isPreview ? 'font-bold text-black' : 'font-normal text-slate-600',
            )}
          >
            {testTypeLabel} Params<span className="text-red-500">*</span>
          </button>
          <button
            type="button"
            onClick={() => setIsPreview(true)}
            className={cn(
              'ml-1 cursor-pointer border-0 bg-transparent p-0 text-left text-sm',
              isPreview ? 'font-bold text-black' : 'font-normal text-slate-600',
            )}
          >
            {testTypeLabel} Preview
          </button>
        </div>

        {isPreview ? (
          <div className="min-w-0">
            <iframe
              title="Test email preview"
              className="min-h-[200px] w-full border-2 border-solid border-slate-300"
              srcDoc={previewSrcDoc}
              sandbox="allow-same-origin"
            />
          </div>
        ) : null}

        <form
          id="send-test-mail-form"
          onSubmit={handleSubmit}
          className={cn('flex flex-col gap-5', isPreview && 'hidden')}
          data-testid="send-test-mail-form"
          noValidate
        >
          <div>
            <label
              className="mb-2 block text-sm font-semibold text-black"
              htmlFor="toAddress"
            >
              To<span className="text-red-500">*</span>
            </label>
            {testType === 'SMS' ? (
              <input
                id="toAddress"
                type="text"
                autoComplete="off"
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                value={toAddress}
                onChange={(e) => {
                  setToAddress(e.target.value)
                  if (testType === 'SMS') {
                    const emsg = validateSmsPhone(e.target.value)
                    setLocalError(emsg)
                  } else {
                    setLocalError('')
                  }
                }}
                placeholder="Phone"
                data-testid="send-test-to-sms"
              />
            ) : (
              <input
                id="toAddress"
                type="text"
                autoComplete="off"
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                value={toAddress}
                onChange={(e) => {
                  setToAddress(e.target.value)
                  setLocalError('')
                }}
                placeholder="TO*"
                data-testid="send-test-to"
              />
            )}
          </div>

          {variableKeys.map((key) => (
            <div key={key}>
              <label
                className="mb-2 block text-sm font-semibold text-black"
                htmlFor={key}
              >
                ${key}
              </label>
              <input
                id={key}
                type="text"
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
                value={bodyParams[key] ?? ''}
                onChange={(e) => setBodyParams((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={key}
                data-testid={`send-test-var-${key}`}
              />
            </div>
          ))}
        </form>
      </div>
    </OnScreenModal>
  )
}
